import { useEffect, useState } from 'react';
import { Button, Dropdown, Tooltip } from 'antd';
import { useToast } from '../../../../../../hooks/useToast';
import { coreAPI } from '../../../../../../API/core';
import { ChevronDown } from '../../../../../shared/components/Icons/ChevronDown';

type MeetingTypeSelectorItemsProps = {
  includeShowAll?: boolean;
};

export const MeetingTypeList: string[] = [
  'Pre-Sales / Discovery',
  'Pre-Sales / Qualifying',
  'Pre-Sales / Closing',
  'Post-Sales / Onboarding + Implementation',
  'Post-Sales / Progress Sync',
  'Post-Sales / Issue or Escalation',
  'Post-Sales / Strategic Review',
  'Post-Sales / Renewal Prep',
  'Post-Sales / Training',
  'Internal / 1:1',
  'Internal / Account Related',
  'Internal / Team',
  'Internal / Cross Functional',
  'External / Consultant',
  'External / Vendor',
  'External / User Research',
  'Other',
];

export const MeetingTypeSelectorItems = (
  setMeetingType: (v: string) => void,
  { includeShowAll }: MeetingTypeSelectorItemsProps = {},
) =>
  [
    {
      label: 'Meeting Type',
      key: '0',
      disabled: true,
    },
    {
      label: 'Pre-Sales',
      key: 'Pre-Sales',
      children: [
        ...(includeShowAll ? [{ label: 'Select All', key: 'Pre-Sales / Select All' }] : []),
        { label: 'Discovery', key: 'Discovery' },
        { label: 'Qualifying', key: 'Qualifying' },
        { label: 'Closing', key: 'Closing' },
      ],
    },
    {
      label: 'Post-Sales',
      key: 'Post-Sales',
      children: [
        ...(includeShowAll ? [{ label: 'Select All', key: 'Post-Sales / Select All' }] : []),
        {
          label: 'Onboarding + Implementation',
          key: 'Onboarding + Implementation',
        },
        { label: 'Progress Sync', key: 'Progress Sync' },
        { label: 'Issue or Escalation', key: 'Issue or Escalation' },
        { label: 'Strategic Review', key: 'Strategic Review' },
        { label: 'Renewal Prep', key: 'Renewal Prep' },
        { label: 'Training', key: 'Training' },
      ],
    },
    {
      label: 'Internal',
      key: 'Internal',
      children: [
        ...(includeShowAll ? [{ label: 'Select All', key: 'Internal / Select All' }] : []),
        {
          label: '1:1',
          key: '1:1',
        },
        { label: 'Account Related', key: 'Account Related' },
        { label: 'Team', key: 'Team' },
        { label: 'Cross Functional', key: 'Cross Functional' },
      ],
    },
    {
      label: 'External',
      key: 'External',
      children: [
        ...(includeShowAll ? [{ label: 'Select All', key: 'External / Select All' }] : []),
        { label: 'Consultant', key: 'Consultant' },
        { label: 'Vendor', key: 'Vendor' },
        { label: 'User Research', key: 'User Research' },
      ],
    },
    {
      label: 'Other',
      key: 'Other',
    },
  ].map(o => {
    if (!o.children || o.children.length === 0) {
      return {
        ...o,
        onClick: () => {
          setMeetingType(o.key);
        },
      };
    } else {
      return {
        ...o,
        children: o.children?.map(c => ({
          ...c,
          onClick: () => {
            if (c.key.includes('Select All')) {
              o.children.forEach(child => {
                setMeetingType([o.key, child.key].join(' / '));
              });

              return;
            }

            setMeetingType([o.key, c.key].join(' / '));
          },
        })),
      };
    }
  });

interface Props {
  meetingType: string | null;
  eventId?: string;
  size?: 'small' | 'middle' | 'large';
  hasPermissions?: boolean;
  showAll?: boolean;
  onChange?: (type: string) => void;
  selectOnly?: boolean;
}

export const MeetingTypeSelector: React.FC<Props> = ({
  meetingType: meetingTypeProps,
  eventId,
  size = 'small',
  hasPermissions = true,
  showAll = false,
  onChange,
  selectOnly = false,
}) => {
  const [meetingType, setMeetingType] = useState(meetingTypeProps);
  const [loading, setLoading] = useState(false);

  const { success, error: errorToast } = useToast();

  const meetingTypeDisplayName = meetingType || 'Meeting Type';

  const handleMeetingTypeChanged = (type: string) => {
    if (!selectOnly) {
      setMeetingType(type);
    }

    if (onChange) {
      onChange(type);
      return;
    }

    if (!eventId) return;

    setLoading(true);
    const coreApi = new coreAPI();
    coreApi
      .updateEvent({
        id: eventId,
        type,
      })
      .then(() => success(`Meeting type changed successfully`))
      .catch(() => errorToast(`There was an error changing the Meeting Type`))
      .finally(() => setLoading(false));
  };

  const items = MeetingTypeSelectorItems(setMeetingType);

  if (showAll) {
    items.splice(0, 1, {
      label: 'All Meeting Type',
      key: 'All Meeting Type',
      onClick: () => setMeetingType('All Meeting Type'),
    });
  }

  useEffect(() => {
    if (meetingType !== meetingTypeProps) handleMeetingTypeChanged(meetingType || '');
  }, [meetingType]);

  useEffect(() => {
    setMeetingType(meetingTypeProps);
  }, [meetingTypeProps]);

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
      <Dropdown menu={{ items }} disabled={loading || !hasPermissions}>
        <Button
          disabled={loading}
          loading={loading}
          size={size}
          style={{
            display: 'flex',
            alignItems: 'center',
            width: '180px',
            justifyContent: 'space-between',
          }}
        >
          <span style={{ display: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            <Tooltip title={meetingTypeDisplayName} placement="top">
              {meetingTypeDisplayName}
            </Tooltip>
          </span>
          <ChevronDown />
        </Button>
      </Dropdown>
    </div>
  );
};
